import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import ObjectProxy from '@ember/object/proxy';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import type FlowModel from 'garaje/models/flow';
import type MailerTemplateModel from 'garaje/models/mailer-template';
import _uniqBy from 'lodash/uniqBy';

interface ManageRecipientsModalComponentSignature {
  Args: {
    flows: FlowModel[];
    mailerTemplates: MailerTemplateModel[];
    onSave: (templates: MailerTemplateModel[], flows: FlowModel[]) => Promise<void>;
  };
}

interface SkipOption {
  title: string;
  identifier: string;
  isSkipOption: boolean;
}

/**
 * All of this complexity is because we cannot save a mailer-template relationships via a flow
 * This ObjectProxy computes the selected, saved and default templates for a given flow and provides
 * information about which templates need to be saved when the selected template is updated
 */
class FlowProxy extends ObjectProxy<FlowModel> {
  @tracked mailerTemplates!: MailerTemplateModel[];
  @tracked templateOptions!: Array<MailerTemplateModel | SkipOption>;

  @tracked _selectedTemplate!: MailerTemplateModel | SkipOption;

  get defaultTemplate() {
    return this.mailerTemplates.find((mailerTemplate) => {
      return mailerTemplate.envoyDefault;
    });
  }

  get savedTemplate() {
    const { defaultTemplate, mailerTemplates } = this;
    const matchedTemplate = mailerTemplates.find((mailerTemplate) =>
      isPresent(mailerTemplate.flows.content.find((flow) => flow.id === this.content?.id)),
    );
    return matchedTemplate || defaultTemplate;
  }

  get selectedTemplate() {
    if (this.content?.mailerTemplatesToSkip.length) {
      // we are skipping the email, pre select the skip option
      const skippedOption = this.templateOptions.find(
        (option) => 'isSkipOption' in option && this.content?.mailerTemplatesToSkip.includes(option.identifier),
      );

      if (skippedOption) return skippedOption;
    }

    return this._selectedTemplate || this.savedTemplate || this.defaultTemplate;
  }

  set selectedTemplate(template: MailerTemplateModel | SkipOption) {
    this._selectedTemplate = template;
  }

  get isDirty() {
    if ('isSkipOption' in this.selectedTemplate) return false;
    return this.selectedTemplate !== this.savedTemplate;
  }

  updateRelationships(): MailerTemplateModel[] {
    const { selectedTemplate, savedTemplate, isDirty, content } = this;
    if (!isDirty) return [];

    const updatedTemplates: MailerTemplateModel[] = [];

    if (savedTemplate) {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      savedTemplate.flows.content.removeObject(content!);
      updatedTemplates.push(savedTemplate);
    }

    // if skipping leave the rel intact
    if (selectedTemplate && !('isSkipOption' in selectedTemplate)) {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      selectedTemplate.flows.content.addObject(content!);
      updatedTemplates.push(selectedTemplate);
    }

    return updatedTemplates;
  }
}

export default class ManageRecipientsModalComponent extends Component<ManageRecipientsModalComponentSignature> {
  @tracked flows;

  constructor(owner: unknown, args: ManageRecipientsModalComponentSignature['Args']) {
    super(owner, args);
    const { flows, mailerTemplates } = this.args;
    this.flows = flows.map((flow) =>
      FlowProxy.create({ templateOptions: this.templateOptions, mailerTemplates, content: flow }),
    );
    registerDestructor(this, this.#cleanupFlowChanges);
  }

  get hasDirtyFlows(): boolean {
    return this.flows.some((flow) => flow.isDirty || flow.content?.hasDirtyAttributes);
  }

  /**
   * identifiers the FE supports for skipping the email
   */
  static SUPPORTED_SKIP_IDENTIFIERS = ['welcome'];

  get mailerThatCanBeSkipped(): MailerTemplateModel | undefined {
    return this.args.mailerTemplates.find(
      (template) =>
        ManageRecipientsModalComponent.SUPPORTED_SKIP_IDENTIFIERS.includes(template.identifier) &&
        template.envoyDefault,
    );
  }

  get templateOptions(): Array<MailerTemplateModel | SkipOption> {
    const mailerTemplates = this.args.mailerTemplates;
    const mailerThatCanBeSkipped = this.mailerThatCanBeSkipped;
    if (mailerThatCanBeSkipped) {
      const skippableTemplates = [
        {
          title: `Do not send ${this.mailerThatCanBeSkipped.title.toLocaleLowerCase()} email`,
          identifier: this.mailerThatCanBeSkipped.identifier,
          isSkipOption: true,
        },
      ];
      return [...mailerTemplates, ...skippableTemplates];
    }

    return mailerTemplates;
  }

  @action
  onChange(flow: FlowProxy, template: MailerTemplateModel): void {
    if (!flow.content) return;

    this.#computeSkippedTemplates(flow, template);

    flow.selectedTemplate = template;
  }

  saveRecipientsTask = dropTask(async () => {
    const { flows } = this;
    const { onSave } = this.args;

    const templatesToSave = _uniqBy(flows.map((flow) => flow.updateRelationships()).flat(), 'id');
    const flowsToSave = flows.map((flow) => flow.content).filter((flow) => flow.hasDirtyAttributes);

    await onSave(templatesToSave, flowsToSave);
  });

  #cleanupFlowChanges = () => {
    this.flows.forEach((flow) => {
      if (flow.isDirty) flow.content.rollbackAttributes();
    });
  };

  #computeSkippedTemplates(flow: FlowProxy, template: MailerTemplateModel | SkipOption): void {
    if (!flow.content) return;
    if (!this.mailerThatCanBeSkipped) return;

    const mailerTemplatesToSkip = flow.content.mailerTemplatesToSkip ?? [];
    const isSkipOption = 'isSkipOption' in template && template.isSkipOption;
    const emailSkipped = mailerTemplatesToSkip.includes(this.mailerThatCanBeSkipped.identifier);

    if (isSkipOption && !emailSkipped) {
      // add the email template to the skip list because the skip option is selected
      flow.content.mailerTemplatesToSkip = [...mailerTemplatesToSkip, this.mailerThatCanBeSkipped.identifier];
    } else if (!isSkipOption && emailSkipped) {
      // remove the email template from the skip list because a template is being assigned
      flow.content.mailerTemplatesToSkip = mailerTemplatesToSkip.filter(
        (item) => item !== this.mailerThatCanBeSkipped!.identifier,
      );
    }
  }
}
